/* eslint-disable no-use-before-define */
import '../style/tailwind.css';
import '../style/dev-controls.css';
import { initLogoResponsiveness } from '../js/logo-responsive.js';
import { initNavGradient } from '../js/nav-gradient.js';
import { gradientStore, getGradientColors } from '../js/gradient-store.js';
import { initHeaderHexGrid } from '../js/header-hex-grid.js';
import '../js/music-service-tabs.js';
import defaultSettings from '../data/default-settings.json';
import CoreWebVitalsMonitor from '../js/core-web-vitals.js';

import posthog from 'posthog-js';

// Initialize PostHog
try {
  const POSTHOG_KEY = process.env.POSTHOG_KEY;
  const POSTHOG_HOST = process.env.POSTHOG_HOST || 'https://us.i.posthog.com';

  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
    });
    posthog.capture('$pageview');
  }
} catch {
  /* ignore PostHog init errors */
}

// Initialize Core Web Vitals monitoring
let webVitalsMonitor = null;

try {
  webVitalsMonitor = new CoreWebVitalsMonitor({
    reportToPostHog: true,
    reportToConsole: process.env.NODE_ENV === 'development',
  });
} catch (error) {
  console.warn('Failed to initialize Core Web Vitals monitoring:', error);
}

// Track link clicks (delegated)
document.addEventListener('click', e => {
  const target = e.target.closest('a');

  if (!target) {
    return;
  }
  try {
    posthog.capture('link_click', {
      href: target.getAttribute('href'),
      text: (target.textContent || '').trim().slice(0, 200),
    });
  } catch {
    /* ignore capture errors */
  }
});

// Developer controls source-of-truth. Default values loaded from default-settings.json
// (no localStorage needed).
let devSettings = { ...defaultSettings };
let hexHeroController = null;

// Dev UI: toggle + sliders for live tuning
function initDevControls() {
  const params = new URLSearchParams(window.location.search || '');

  if (params.get('dev') !== '1') {
    return;
  }
  // Intentionally do not auto-load localStorage. Use the buttons to copy/clear.

  const panel = document.createElement('div');

  panel.id = 'dev-controls';
  panel.classList.add('dev-control-panel');

  const toggle = document.createElement('button');

  toggle.type = 'button';
  toggle.setAttribute('aria-label', 'Toggle developer controls');
  toggle.textContent = '🎚️';
  toggle.classList.add('dev-gear-button');
  toggle.addEventListener('click', () => {
    panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
  });

  function sliderRow(label, key, min, max, step, unit = '') {
    const row = document.createElement('div');

    row.classList.add('control-row');

    const title = document.createElement('label');

    title.textContent = `${label}: `;
    title.classList.add('control-label');

    const input = document.createElement('input');

    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(devSettings[key]);
    input.classList.add('control-slider');

    const value = document.createElement('span');

    value.textContent = `${devSettings[key]}${unit}`;
    value.classList.add('control-value');
    input.addEventListener('input', () => {
      const num = Number(input.value);

      devSettings[key] = num;
      value.textContent = `${num}${unit}`;

      gradientStore.dispatch({
        type: 'UPDATE_SETTINGS',
        payload: { [key]: num },
      });

      try {
        localStorage.setItem('devSettings', JSON.stringify(devSettings));
      } catch {
        /* ignore */
      }
    });
    row.appendChild(title);
    row.appendChild(input);
    row.appendChild(value);
    panel.appendChild(row);
  }

  const header = document.createElement('div');

  header.textContent = 'Developer Controls';
  header.style.fontWeight = '600';
  header.style.marginBottom = '8px';
  panel.appendChild(header);

  // Actions row: Copy / Clear local storage
  const actions = document.createElement('div');

  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.margin = '6px 0 10px 0';

  const copyBtn = document.createElement('button');

  copyBtn.type = 'button';
  copyBtn.textContent = 'Copy Settings';
  copyBtn.style.padding = '4px 8px';
  copyBtn.style.border = '1px solid #555';
  copyBtn.style.borderRadius = '6px';
  copyBtn.style.background = '#333';
  copyBtn.style.color = '#fff';
  copyBtn.addEventListener('click', async () => {
    const text = JSON.stringify(devSettings, null, 2);

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');

      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
  });

  const clearBtn = document.createElement('button');

  clearBtn.type = 'button';
  clearBtn.textContent = 'Clear Local Storage';
  clearBtn.style.padding = '4px 8px';
  clearBtn.style.border = '1px solid #555';
  clearBtn.style.borderRadius = '6px';
  clearBtn.style.background = '#333';
  clearBtn.style.color = '#fff';
  clearBtn.addEventListener('click', () => {
    try {
      localStorage.removeItem('devSettings');
    } catch {
      /* ignore */
    }
  });

  actions.appendChild(copyBtn);
  actions.appendChild(clearBtn);
  panel.appendChild(actions);

  sliderRow('Speed', 'speedScale', 0, 2, 0.01, '×');
  sliderRow('Hue Span (deg)', 'gradSpanDeg', 100, 340, 5, '°');
  sliderRow('Saturation Base', 'satBase', 0, 100, 1, '%');
  sliderRow('Saturation Amp', 'satAmp', 0, 50, 1, '%');
  sliderRow('Luminosity Base', 'lightBase', 0, 100, 1, '%');
  sliderRow('Luminosity Amp', 'lightAmp', 0, 50, 1, '%');
  sliderRow('Hex Opacity Min', 'hexOpacityMin', 0, 1, 0.01);
  sliderRow('Hex Opacity Max', 'hexOpacityMax', 0, 1, 0.01);
  sliderRow('Hex Center Radius', 'hexCenterRadius', 0.1, 1.5, 0.01);
  sliderRow('Hex Center Ease', 'hexCenterEase', 0.2, 4, 0.1);
  sliderRow('Hex Noise Amp', 'hexNoiseAmp', 0, 1, 0.01);
  sliderRow('Hex Noise Zoom', 'hexNoiseZoom', 0.1, 10, 0.1);
  sliderRow('Hex Noise Ease', 'hexNoiseEase', 0.2, 4, 0.1);
  sliderRow('Hex Noise Speed', 'hexNoiseSpeed', 0, 3, 0.01);
  sliderRow('Hex Scale', 'hexScale', 0.5, 1.8, 0.01, '×');

  // Add FPS display
  const fpsRow = document.createElement('div');

  fpsRow.classList.add('control-row');

  const fpsLabel = document.createElement('span');

  fpsLabel.textContent = 'FPS: ';
  fpsLabel.classList.add('control-label');

  const fpsDisplay = document.createElement('span');

  fpsDisplay.textContent = '-- FPS';
  fpsDisplay.classList.add('control-value', 'fps-display');

  // Store reference for FPS updates
  fpsCounter.element = fpsDisplay;

  fpsRow.appendChild(fpsLabel);
  fpsRow.appendChild(fpsDisplay);
  panel.appendChild(fpsRow);

  document.body.appendChild(panel);
  document.body.appendChild(toggle);
}

const nav = document.querySelector('nav');
// Targets for upcoming masked gradient work

// Utility helpers
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const debounce = (fn, delay = 120) => {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const hslToRgb = ({ h, s, l }) => {
  const hue = ((h % 360) + 360) % 360;
  const saturation = clamp(s, 0, 100) / 100;
  const lightness = clamp(l, 0, 100) / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = lightness - chroma / 2;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hue < 60) {
    red = chroma;
    green = x;
  } else if (hue < 120) {
    red = x;
    green = chroma;
  } else if (hue < 180) {
    green = chroma;
    blue = x;
  } else if (hue < 240) {
    green = x;
    blue = chroma;
  } else if (hue < 300) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  return {
    r: Math.round((red + match) * 255),
    g: Math.round((green + match) * 255),
    b: Math.round((blue + match) * 255),
  };
};

const mixRgb = (start, end, t) => ({
  r: Math.round(start.r + (end.r - start.r) * t),
  g: Math.round(start.g + (end.g - start.g) * t),
  b: Math.round(start.b + (end.b - start.b) * t),
});

const toRgbString = ({ r, g, b }) => `rgb(${r}, ${g}, ${b})`;

function updateNavToggleHexColors() {
  const toggles = document.querySelectorAll('nav.top-bar .nav-dropdown-toggle');

  if (!toggles || toggles.length === 0) {
    return;
  }

  const { primaryColor, peekColor } = getGradientColors(gradientStore.getState());
  const startColor = hslToRgb(primaryColor);
  const endColor = hslToRgb(peekColor);

  toggles.forEach(toggle => {
    const t = clamp(Number(toggle.style.getPropertyValue('--slice-mid') || '0'), 0, 1);
    const swatchColor = mixRgb(startColor, endColor, t);

    toggle.style.setProperty('--nav-swatch-color', toRgbString(swatchColor));
  });
}

let navToggleHexUpdateQueued = false;
const queueNavToggleHexColorUpdate = () => {
  if (navToggleHexUpdateQueued) {
    return;
  }
  navToggleHexUpdateQueued = true;
  requestAnimationFrame(() => {
    navToggleHexUpdateQueued = false;
    updateNavToggleHexColors();
  });
};

let unsubscribeNavToggleHex = gradientStore.subscribe(queueNavToggleHexColorUpdate);

/**
 * Measure nav links and set normalized start/end CSS variables (0–1)
 * and pixel-based positioning for future masked gradient usage.
 */
function measureAndSetNavLinkSlices() {
  if (!nav) {
    return;
  }

  const navRect = nav.getBoundingClientRect();
  const navWidth = Math.max(1, navRect.width);

  if (!Number.isFinite(navWidth) || navWidth <= 0) {
    return;
  }

  const links = document.querySelectorAll(
    'nav.top-bar a:not(.no-highlight), nav.top-bar .nav-dropdown-toggle'
  );

  if (!links || links.length === 0) {
    return;
  }

  links.forEach(link => {
    const rect = link.getBoundingClientRect();
    const leftPx = rect.left - navRect.left;
    const rightPx = rect.right - navRect.left;
    const start = clamp(leftPx / navWidth, 0, 1);
    const end = clamp(rightPx / navWidth, 0, 1);

    link.style.setProperty('--nav-w', `${navWidth}px`);
    link.style.setProperty('--slice-left', `${leftPx}px`);
    link.style.setProperty('--slice-start', `${start}`);
    link.style.setProperty('--slice-end', `${end}`);
    link.style.setProperty('--slice-mid', `${(start + end) / 2}`);

    if (link.classList.contains('nav-dropdown-toggle')) {
      link.style.backgroundImage = 'none';
      link.style.backgroundSize = '';
      link.style.backgroundPosition = '';
      link.style.backgroundRepeat = '';

      return;
    }

    // Set static positioning properties; only the image updates per frame
    link.style.backgroundRepeat = 'no-repeat';
    link.style.backgroundSize = 'var(--nav-w) 100%';
    link.style.backgroundPosition = 'calc(var(--slice-left) * -1) 0px';
    // Use gradient from parent CSS variable to avoid per-frame link writes
    link.style.backgroundImage = 'var(--nav-gradient)';

    // Bind hover handlers once to animate background-size X to the element width
    if (!link.dataset.maskHoverBound) {
      link.addEventListener('mouseenter', () => {
        const rectHover = link.getBoundingClientRect();
        const elementWidth = Math.max(1, Math.round(rectHover.width));

        link.style.backgroundSize = `${elementWidth}px 100%`;
        // Fill the element entirely when zoomed; avoid progressive empty space
        link.style.backgroundPosition = '0px 0px';
      });
      link.addEventListener('mouseleave', () => {
        link.style.backgroundSize = 'var(--nav-w) 100%';
        link.style.backgroundPosition = 'calc(var(--slice-left) * -1) 0px';
      });
      link.dataset.maskHoverBound = '1';
    }
  });

  updateNavToggleHexColors();
}

// Initial measurement and resize handling
const debouncedMeasure = debounce(measureAndSetNavLinkSlices, 150);
let resizeObserver = null;

window.addEventListener('load', debouncedMeasure);
window.addEventListener('resize', debouncedMeasure);

// Create ResizeObserver only once
if (window.ResizeObserver && nav && !resizeObserver) {
  resizeObserver = new ResizeObserver(() => debouncedMeasure());
  resizeObserver.observe(nav);
}

let colorPartyActive = true;

// FPS counter for dev mode
let fpsCounter = {
  frames: 0,
  lastTime: performance.now(),
  fps: 0,
  element: null,
};

function updateFPS() {
  const now = performance.now();

  fpsCounter.frames++;

  if (now - fpsCounter.lastTime >= 1000) {
    fpsCounter.fps = Math.round((fpsCounter.frames * 1000) / (now - fpsCounter.lastTime));
    fpsCounter.frames = 0;
    fpsCounter.lastTime = now;

    if (fpsCounter.element) {
      fpsCounter.element.textContent = `${fpsCounter.fps} FPS`;
    }
  }
}

const stopTheLights = document.querySelectorAll('.stop-the-lights');

// Enable color-party for SVG elements in nav
const colorParty = [
  Array.from(document.querySelectorAll('linearGradient:not([id="backgroundFadeGradient"])')),
  Array.from(document.querySelectorAll('.nav-color-party')),
];

colorParty.forEach(selectorGroup => {
  selectorGroup.mouseOver = false;

  const mouseover = () => {
    selectorGroup.mouseOver = true;
  };

  const mouseout = () => {
    setTimeout(() => {
      selectorGroup.mouseOver = false;
    }, 3000);
  };

  if (!selectorGroup.colorPartyEnabled) {
    selectorGroup.colorPartyEnabled = true;
    selectorGroup.forEach(selector => {
      // TODO: Nulls end up in the array when navigation has no active item
      // on the album pages.
      if (!selector) {
        return;
      }
      selector.classList.add('color-party');
      selector.addEventListener('mouseover', mouseover);
      selector.addEventListener('mouseout', mouseout);
    });
  }
});

// Accessibility: Check for reduced motion preference
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function handleReducedMotionPreference() {
  if (prefersReducedMotion.matches) {
    colorPartyActive = false;
    // Reset elements to default state when animations are disabled
    colorParty.forEach(selectorGroup => {
      selectorGroup.forEach(selector => {
        if (selector && selector.classList.contains('color-party')) {
          selector.style.background = '';
          selector.style.color = '';
          selector.style.borderColor = '';
        }
      });
    });
  } else {
    colorPartyActive = true;
    measureAndSetNavLinkSlices();
  }
}

// Initialize and react to preference changes
handleReducedMotionPreference();
prefersReducedMotion.addEventListener('change', handleReducedMotionPreference);

// Listen for animation control changes from nav controls
window.addEventListener('animationSpeedChange', e => {
  const { enabled } = e.detail;

  colorPartyActive = enabled;

  if (!enabled) {
    // Reset elements to default state when animations are disabled
    colorParty.forEach(selectorGroup => {
      selectorGroup.forEach(selector => {
        if (selector && selector.classList.contains('color-party')) {
          selector.style.background = '';
          selector.style.color = '';
          selector.style.borderColor = '';
        }
      });
    });
  } else {
    measureAndSetNavLinkSlices();
  }
});

// Initialize dev controls after DOM is ready
window.addEventListener('load', initDevControls);

// Public user-facing controls in nav - always available
function initPublicNavControls() {
  // Create user gear button - always available for users
  const speedGear = document.createElement('button');

  speedGear.type = 'button';
  speedGear.setAttribute('aria-label', 'Toggle speed controls');
  speedGear.textContent = '⚙️';
  speedGear.classList.add('speed-gear-button');

  document.body.appendChild(speedGear);

  // Check if dev controls are present and adjust positioning
  const checkDevControls = () => {
    const devGear = document.querySelector('.dev-gear-button');

    if (devGear) {
      speedGear.classList.add('with-dev-controls');
    } else {
      speedGear.classList.remove('with-dev-controls');
    }
  };

  // Check immediately and after a short delay (in case dev controls load later)
  checkDevControls();
  setTimeout(checkDevControls, 100);

  // Create speed control panel matching dev controls style
  const speedPanel = document.createElement('div');

  speedPanel.classList.add('speed-control-panel');

  // Speed control row
  const speedRow = document.createElement('div');

  speedRow.classList.add('control-row');

  const speedLabel = document.createElement('span');

  speedLabel.textContent = 'Speed:';
  speedLabel.classList.add('control-label');

  const speedSlider = document.createElement('input');

  speedSlider.type = 'range';
  speedSlider.min = '0';
  speedSlider.max = '1';
  speedSlider.step = '0.01';
  speedSlider.value = '0.12'; // Default 12%
  speedSlider.classList.add('control-slider');

  const speedValue = document.createElement('span');

  speedValue.textContent = '12%';
  speedValue.classList.add('control-value');

  speedRow.appendChild(speedLabel);
  speedRow.appendChild(speedSlider);
  speedRow.appendChild(speedValue);
  speedPanel.appendChild(speedRow);

  // Animation enable/disable checkbox
  const enableRow = document.createElement('div');

  enableRow.classList.add('control-row');

  const enableCheckbox = document.createElement('input');

  enableCheckbox.type = 'checkbox';
  enableCheckbox.id = 'animation-enabled';
  enableCheckbox.checked = true; // Default enabled
  enableCheckbox.classList.add('control-checkbox');

  const enableLabel = document.createElement('label');

  enableLabel.htmlFor = 'animation-enabled';
  enableLabel.textContent = 'Enable Animation';
  enableLabel.classList.add('control-checkbox-label');

  enableRow.appendChild(enableCheckbox);
  enableRow.appendChild(enableLabel);
  speedPanel.appendChild(enableRow);

  document.body.appendChild(speedPanel);

  // Wire up speed slider to Redux
  const updateSpeedFromSlider = value => {
    const pctVal = Math.round(value * 100);

    speedValue.textContent = `${pctVal}%`;

    // Map 0..1 → 0..2 scale (same as original logic)
    devSettings.speedScale = value * 2;

    // Update Redux store
    gradientStore.dispatch({
      type: 'UPDATE_SETTINGS',
      payload: { speedScale: devSettings.speedScale },
    });

    // Save to localStorage
    localStorage.setItem('nav-animation-speed', value.toString());
  };

  speedSlider.addEventListener('input', e => {
    const value = Number(e.target.value || '0.12');

    updateSpeedFromSlider(value);
  });

  // Wire up animation checkbox to Redux
  const updateAnimationEnabled = enabled => {
    enableCheckbox.checked = enabled;
    speedSlider.disabled = !enabled;

    // Update Redux store
    gradientStore.dispatch({
      type: 'TOGGLE_ANIMATION',
      payload: enabled,
    });

    // Restart animation loop if enabled, stop if disabled
    if (enabled) {
      startGradientAnimation();
    } else {
      stopGradientAnimation();
    }

    // Save to localStorage
    localStorage.setItem('nav-animation-enabled', enabled.toString());
  };

  enableCheckbox.addEventListener('change', e => {
    updateAnimationEnabled(e.target.checked);
  });

  // Load saved preferences
  const savedSpeed = localStorage.getItem('nav-animation-speed');
  const savedEnabled = localStorage.getItem('nav-animation-enabled');

  if (savedSpeed !== null) {
    speedSlider.value = savedSpeed;
    updateSpeedFromSlider(Number(savedSpeed));
  }

  if (savedEnabled !== null) {
    updateAnimationEnabled(savedEnabled === 'true');
  }

  // Toggle panel visibility
  let speedPanelVisible = false;

  speedGear.addEventListener('click', () => {
    speedPanelVisible = !speedPanelVisible;
    speedPanel.style.display = speedPanelVisible ? 'block' : 'none';
    speedGear.setAttribute('aria-expanded', String(speedPanelVisible));
  });

  // Old nav controls removed - now using gear button system above
  // The HTML nav controls are still in the templates but won't be functional
  // when ?dev=1 is not set, providing a cleaner dev-only experience
}

window.addEventListener('load', initPublicNavControls);

// IntersectionObserver removed - Redux animation is efficient enough without this optimization

stopTheLights.forEach(el => {
  el.addEventListener('click', e => {
    colorPartyActive = !colorPartyActive;
    e.target.innerHTML = `${colorPartyActive ? '⏹' : '▶️'} 🌈`;
    e.target.setAttribute(
      'aria-label',
      `${colorPartyActive ? 'Stop' : 'Start'} color party animations`
    );

    // Restart or stop animation based on new state
    if (colorPartyActive) {
      startGradientAnimation();
    } else {
      stopGradientAnimation();
    }
  });

  // Add keyboard accessibility
  el.addEventListener('keydown', e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      el.click();
    }
  });
});

// Add keyboard shortcut to toggle color-party (Ctrl/Cmd + Shift + C)
document.addEventListener('keydown', e => {
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
    e.preventDefault();
    colorPartyActive = !colorPartyActive;

    // Update all stop-the-lights buttons
    stopTheLights.forEach(el => {
      el.innerHTML = `${colorPartyActive ? '⏹' : '▶️'} 🌈`;
      el.setAttribute(
        'aria-label',
        `${colorPartyActive ? 'Stop' : 'Start'} color party animations`
      );
    });

    // Restart or stop animation based on new state
    if (colorPartyActive) {
      startGradientAnimation();
    } else {
      stopGradientAnimation();
    }
  }
});

// Navigation dropdown functionality
function initNavigation() {
  const dropdowns = document.querySelectorAll('.nav-dropdown');

  dropdowns.forEach(dropdown => {
    const toggle = dropdown.querySelector('.nav-dropdown-toggle');

    if (toggle) {
      toggle.addEventListener('click', function (e) {
        // Prevent default to avoid any link behavior
        e.preventDefault();
        e.stopPropagation();

        // Toggle dropdown on all devices
        dropdown.classList.toggle('active');

        // Close other dropdowns
        dropdowns.forEach(otherDropdown => {
          if (otherDropdown !== dropdown) {
            otherDropdown.classList.remove('active');
          }
        });
      });

      // Add keyboard support (Enter and Space keys)
      toggle.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();

          // Toggle dropdown on all devices
          dropdown.classList.toggle('active');

          // Close other dropdowns
          dropdowns.forEach(otherDropdown => {
            if (otherDropdown !== dropdown) {
              otherDropdown.classList.remove('active');
            }
          });
        }
      });
    }
  });

  // Close dropdowns when clicking outside
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.nav-dropdown')) {
      dropdowns.forEach(dropdown => {
        dropdown.classList.remove('active');
      });
    }
  });
}

// Initialize components when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initNavigation();
    initLogoResponsiveness();
    initNavGradient();
    hexHeroController = initHeaderHexGrid();

    // Initialize Redux store with current devSettings and saved preferences
    gradientStore.dispatch({
      type: 'UPDATE_SETTINGS',
      payload: devSettings,
    });

    // Load saved animation state
    const savedEnabled = localStorage.getItem('nav-animation-enabled');

    if (savedEnabled !== null) {
      gradientStore.dispatch({
        type: 'TOGGLE_ANIMATION',
        payload: savedEnabled === 'true',
      });
    }

    startGradientAnimation();
  });
} else {
  initNavigation();
  initLogoResponsiveness();
  initNavGradient();
  hexHeroController = initHeaderHexGrid();

  // Initialize Redux store with current devSettings and saved preferences
  gradientStore.dispatch({
    type: 'UPDATE_SETTINGS',
    payload: devSettings,
  });

  // Load saved animation state
  const savedEnabled = localStorage.getItem('nav-animation-enabled');

  if (savedEnabled !== null) {
    gradientStore.dispatch({
      type: 'TOGGLE_ANIMATION',
      payload: savedEnabled === 'true',
    });
  }

  startGradientAnimation();
}

// Proper Redux animation loop with performance optimizations
let animationId = null;
let isAnimationRunning = false;

function startGradientAnimation() {
  if (isAnimationRunning) {
    return; // Prevent multiple animation loops
  }

  isAnimationRunning = true;

  function animationLoop() {
    const state = gradientStore.getState();
    const shouldAnimate = colorPartyActive && state.animationEnabled;

    if (shouldAnimate) {
      gradientStore.dispatch({ type: 'INCREMENT_GRADIENT' });
    }

    // Only update FPS counter if dev controls are visible and animation is running
    if (shouldAnimate && fpsCounter.element) {
      updateFPS();
    }

    // Continue animation loop only if animation is actually running
    // FPS counter should only run when there's actual animation to measure
    if (shouldAnimate) {
      animationId = requestAnimationFrame(animationLoop);
    } else {
      // Stop animation loop when not needed to save CPU
      isAnimationRunning = false;
      animationId = null;

      // Clear FPS display when animation stops
      if (fpsCounter.element) {
        fpsCounter.element.textContent = '0 FPS';
      }
    }
  }

  animationLoop();
}

function stopGradientAnimation() {
  if (animationId) {
    cancelAnimationFrame(animationId);
    animationId = null;
  }
  isAnimationRunning = false;
}

// Cleanup function to prevent memory leaks
function cleanup() {
  stopGradientAnimation();

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  // Clear FPS counter reference
  if (fpsCounter.element) {
    fpsCounter.element = null;
  }

  // Disconnect Core Web Vitals monitor
  if (webVitalsMonitor) {
    webVitalsMonitor.disconnect();
    webVitalsMonitor = null;
  }

  if (typeof unsubscribeNavToggleHex === 'function') {
    unsubscribeNavToggleHex();
    unsubscribeNavToggleHex = null;
  }

  if (hexHeroController && typeof hexHeroController.destroy === 'function') {
    hexHeroController.destroy();
    hexHeroController = null;
  }
}

// Clean up on page unload
window.addEventListener('beforeunload', cleanup);
window.addEventListener('pagehide', cleanup);

export default colorParty;
